/**
 * Centralized friendly-error mapper.
 *
 * Converts raw error strings (from Supabase Edge Functions, Stripe, network
 * failures, RLS denials, etc.) into a small set of UI-ready descriptors so
 * every surface tells the same story.
 */

export type FriendlyErrorKind =
  | "content_policy"
  | "out_of_credits"
  | "subscription_required"
  | "network"
  | "auth"
  | "rate_limit"
  | "generic";

export interface FriendlyError {
  kind: FriendlyErrorKind;
  title: string;
  subtitle: string;
  /** Suggested CTA label, if a retry/upgrade/topup makes sense. */
  ctaLabel?: string;
  /** Hint to the caller about which CTA to wire. */
  ctaIntent?: "retry" | "topup" | "upgrade" | "refresh";
}

const CONTENT_POLICY_RX =
  /content[_ ]?policy|safety|blocked|prohibited|nsfw|moderation|disallowed/i;
const OUT_OF_CREDITS_RX = /(out of|insufficient|no(t enough)?|0)\s*credits?/i;
const SUBSCRIPTION_RX =
  /(starter|pro|paid|plan|subscription|upgrade required|requires?\s+(starter|pro))/i;
const NETWORK_RX =
  /failed to fetch|network|timeout|timed?\s*out|offline|connection|ECONN|ENET/i;
const AUTH_RX =
  /(not\s*authenticated|unauthori[sz]ed|jwt|session\s*expired|sign\s*in)/i;
const RATE_LIMIT_RX = /rate[_ ]?limit|too many requests|429/i;
const RAW_EDGE_RX =
  /non-2xx|FunctionsHttpError|FunctionsRelayError|FunctionsFetchError/i;

export const toFriendlyError = (
  err: unknown,
  context?: "image" | "text" | "stripe" | "generic",
): FriendlyError => {
  const raw =
    err instanceof Error
      ? err.message
      : typeof err === "string"
        ? err
        : err && typeof err === "object" && "message" in err
          ? String((err as { message: unknown }).message)
          : "";

  if (CONTENT_POLICY_RX.test(raw) && context === "image") {
    return {
      kind: "content_policy",
      title: "Imagen couldn't generate this",
      subtitle:
        "The reference image or prompt may include content the model can't process. Try rephrasing or removing the reference image.",
      ctaLabel: "Try again",
      ctaIntent: "retry",
    };
  }

  if (OUT_OF_CREDITS_RX.test(raw)) {
    return {
      kind: "out_of_credits",
      title: "You're out of credits",
      subtitle: "Top up or upgrade to keep going.",
      ctaLabel: "Top up",
      ctaIntent: "topup",
    };
  }

  if (SUBSCRIPTION_RX.test(raw)) {
    return {
      kind: "subscription_required",
      title: "This is a Pro feature",
      subtitle: "Upgrade to access.",
      ctaLabel: "Upgrade",
      ctaIntent: "upgrade",
    };
  }

  if (NETWORK_RX.test(raw)) {
    return {
      kind: "network",
      title: "Connection issue",
      subtitle: "Check your internet and try again.",
      ctaLabel: "Try again",
      ctaIntent: "retry",
    };
  }

  if (AUTH_RX.test(raw)) {
    return {
      kind: "auth",
      title: "Your session expired",
      subtitle: "Refresh the page and sign in again.",
      ctaLabel: "Refresh",
      ctaIntent: "refresh",
    };
  }

  if (RATE_LIMIT_RX.test(raw)) {
    return {
      kind: "rate_limit",
      title: "Too many requests",
      subtitle: "Give it a moment and try again.",
      ctaLabel: "Try again",
      ctaIntent: "retry",
    };
  }

  if (context === "image" && RAW_EDGE_RX.test(raw)) {
    return {
      kind: "generic",
      title: "Couldn't generate that image",
      subtitle:
        "Try a different style, remove the reference image, or rephrase your prompt.",
      ctaLabel: "Try again",
      ctaIntent: "retry",
    };
  }

  if (RAW_EDGE_RX.test(raw) || !raw) {
    return {
      kind: "generic",
      title: "Something went wrong",
      subtitle: "Try again, or refresh the page if it persists.",
      ctaLabel: "Try again",
      ctaIntent: "retry",
    };
  }

  // Final fallback — keep the message but wrap it in friendly chrome.
  return {
    kind: "generic",
    title: "Something went wrong",
    subtitle: raw,
    ctaLabel: "Try again",
    ctaIntent: "retry",
  };
};

/** Single-line variant for toasts. */
export const toFriendlyToast = (
  err: unknown,
  context?: "image" | "text" | "stripe" | "generic",
): string => {
  const f = toFriendlyError(err, context);
  return f.kind === "generic" && f.subtitle && f.subtitle !== "Try again, or refresh the page if it persists."
    ? f.subtitle
    : `${f.title} — ${f.subtitle}`;
};